import { safeParseDate } from '../lib/utils';
import { db, handleFirestoreError, OperationType } from './firebase';
import { collection, query, where, limit, getDocs, getDoc, updateDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { startOfDay, isToday, isTomorrow, isPast, format, parseISO } from 'date-fns';

export interface Task {
  id: string;
  caseId: string;
  lawyerId: string;
  title: string;
  dueDate: string;
  status: 'pending' | 'completed';
  createdAt: string;
}

export interface Reminder {
  id: string;
  userId: string;
  workspaceId: string;
  caseId: string;
  type: 'missed_next_date' | 'upcoming_hearing' | 'follow_up' | 'overdue';
  message: string;
  dueDate: string;
  isRead: boolean;
  createdAt: string;
  caseTitle?: string;
}

export const syncReminders = async (userId: string, workspaceId: string) => {
  if (!userId || !workspaceId) return;

  try {
    // 1. Fetch recent cases for the user (bounded; tasks reference case titles from this set)
    const casesRef = collection(db, 'workspaces', workspaceId, 'cases');
    const qCases = query(casesRef, where('lawyerId', '==', userId), limit(500));
    const casesSnap = await getDocs(qCases);
    const cases = casesSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() as object) } as Record<string, unknown> & { id: string }))
      .sort((a, b) => {
        const au = new Date(String(a.updatedAt || a.createdAt || 0)).getTime();
        const bu = new Date(String(b.updatedAt || b.createdAt || 0)).getTime();
        return bu - au;
      })
      .slice(0, 500);

    // 2. Fetch recent tasks for the user (sort client-side — no composite index on lawyerId+createdAt)
    const tasksRef = collection(db, 'workspaces', workspaceId, 'tasks');
    const qTasks = query(tasksRef, where('lawyerId', '==', userId), limit(500));
    const tasksSnap = await getDocs(qTasks);
    const tasks = tasksSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() as object) } as Task))
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 500);

    const pendingReminders: { id: string, data: Omit<Reminder, 'id'> }[] = [];
    const now = new Date();

    // Evaluate Cases for Missed Next Date & Upcoming Hearings
    cases.forEach(c => {
      if (c.nextHearingDate) {
        const hearingDate = safeParseDate(c.nextHearingDate);
        
        if (isToday(hearingDate) || isTomorrow(hearingDate)) {
          // Upcoming hearing
          const type = 'upcoming_hearing';
          const message = isToday(hearingDate) ? `Hearing today for ${c.title}` : `Hearing tomorrow for ${c.title}`;
          const docId = `rem_${userId}_case_${c.id}_${type}_${c.nextHearingDate}`;
          
          pendingReminders.push({
            id: docId,
            data: {
              userId,
              workspaceId: c.workspaceId || c.lawyerId || workspaceId,
              caseId: c.id,
              type,
              message,
              dueDate: c.nextHearingDate,
              isRead: false,
              createdAt: new Date().toISOString(),
              caseTitle: c.title
            }
          });
        } else if (isPast(hearingDate) && !isToday(hearingDate)) {
          // Missed next date
          const type = 'missed_next_date';
          const message = `Next hearing date not entered for ${c.title}`;
          const docId = `rem_${userId}_case_${c.id}_${type}_${c.nextHearingDate}`;
          
          pendingReminders.push({
            id: docId,
            data: {
              userId,
              workspaceId: c.workspaceId || c.lawyerId || workspaceId,
              caseId: c.id,
              type,
              message,
              dueDate: c.nextHearingDate,
              isRead: false,
              createdAt: new Date().toISOString(),
              caseTitle: c.title
            }
          });
        }
      }
    });

    // Evaluate Tasks for Follow-ups & Overdue
    tasks.forEach(t => {
      if (t.status === 'completed') return;

      const dueDate = safeParseDate(t.dueDate);
      
      if (isToday(dueDate)) {
        const type = 'follow_up';
        const message = `Follow-up: ${t.title}`;
        const docId = `rem_${userId}_task_${t.id}_${type}_${t.dueDate}`;
        
        const c = cases.find(c => c.id === t.caseId);
        pendingReminders.push({
          id: docId,
          data: {
            userId,
            workspaceId: (t as any).workspaceId || t.lawyerId || workspaceId,
            caseId: t.caseId,
            type,
            message,
            dueDate: t.dueDate,
            isRead: false,
            createdAt: new Date().toISOString(),
            caseTitle: c?.title || 'Unknown Case'
          }
        });
      } else if (isPast(dueDate) && !isToday(dueDate)) {
        const type = 'overdue';
        const message = `Overdue: ${t.title}`;
        const docId = `rem_${userId}_task_${t.id}_${type}_${t.dueDate}`;
        
        const c = cases.find(c => c.id === t.caseId);
        pendingReminders.push({
          id: docId,
          data: {
            userId,
            workspaceId: (t as any).workspaceId || t.lawyerId || workspaceId,
            caseId: t.caseId,
            type,
            message,
            dueDate: t.dueDate,
            isRead: false,
            createdAt: new Date().toISOString(),
            caseTitle: c?.title || 'Unknown Case'
          }
        });
      }
    });

    // Write new reminders to Firestore only if they don't exist
    const batchPromises = pendingReminders.map(async (rem) => {
      const dRef = doc(db, 'workspaces', (rem.data as any).workspaceId || 'no-ws', 'reminders', rem.id);
      const dSnap = await getDoc(dRef);
      if (!dSnap.exists()) {
        await setDoc(dRef, rem.data);
      }
    });
    
    await Promise.all(batchPromises);

    // Dismiss stale "missed next date" alerts when the case no longer has a past hearing date
    const dismissPromises = cases.map(async (c) => {
      const hearingDate = c.nextHearingDate
        ? safeParseDate(c.nextHearingDate as string)
        : null;
      const stillMissed =
        hearingDate && isPast(hearingDate) && !isToday(hearingDate);
      if (stillMissed) return;

      const qStale = query(
        collection(db, 'workspaces', workspaceId, 'reminders'),
        where('userId', '==', userId),
        where('caseId', '==', c.id),
        where('type', '==', 'missed_next_date'),
        where('isRead', '==', false),
      );
      const staleSnap = await getDocs(qStale);
      await Promise.all(
        staleSnap.docs.map((d) => updateDoc(d.ref, { isRead: true })),
      );
    });
    await Promise.all(dismissPromises);

  } catch (error) {
    console.error("Error syncing reminders:", error);
  }
};

/** Clear unread missed-next-date alerts for one case (e.g. after diary sync from timeline). */
export async function dismissMissedNextDateRemindersForCase(
  workspaceId: string,
  userId: string,
  caseId: string,
): Promise<void> {
  if (!workspaceId || !userId || !caseId) return;
  try {
    const qStale = query(
      collection(db, 'workspaces', workspaceId, 'reminders'),
      where('userId', '==', userId),
      where('caseId', '==', caseId),
      where('type', '==', 'missed_next_date'),
      where('isRead', '==', false),
    );
    const staleSnap = await getDocs(qStale);
    await Promise.all(
      staleSnap.docs.map((d) => updateDoc(d.ref, { isRead: true })),
    );
  } catch (error) {
    console.error('Error dismissing missed next date reminders:', error);
  }
}
